<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import router from '@/router'
import { useGameStore } from '@/stores/gameStore.ts'
import Backend from '@/models/backend.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import { useShipGroupStore } from '@/stores/shipGroupStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const tableStore = useTableStore()
const shipActionStore = useShipActionStore()
const gameStore = useGameStore()
const shipGroupStore = useShipGroupStore()
const mapStore = useMapStore()

/**
 * Variables
 */
const menuRef = ref(null)

/**
 * Props
 */
const props = defineProps({
  lockedToFleetId: {
    type: Number,
    default: 0
  },
  smartPositioning: {
    type: Boolean,
    default: false
  }
})

/**
 * Functions
 */
function toggleMenu() {
  if (gameStore.pullDownMenuVisible === 'shipMultiSelectMenu') {
    closeMenu()
  } else {
    gameStore.pullDownMenuVisible = 'shipMultiSelectMenu'
  }
}

// Perform actions on the map with selected ships
const actionMap = () => {
  if (tableStore.ships.selectedRowIds.length > 0) {
    logger.debug('actionMap', tableStore.ships.selectedRowIds)
    shipActionStore.initShipsFromIds(tableStore.ships.selectedRowIds)
    tableStore.ships.selectedRowIds = []
    tableStore.ships.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()

    router.push('/map/galaxy/')
  }
}

/**
 * Assign ships to a fleet
 */
const assignToFleet = () => {
  if (tableStore.ships.selectedRowIds.length > 0) {
    gameStore.modalType = 'assignToFleet'
    gameStore.modalData = tableStore.ships.selectedRowIds
    tableStore.ships.selectedRowIds = []
    tableStore.ships.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const detachFromFleet = () => {
  if (tableStore.ships.selectedRowIds.length > 0) {
    const backend = new Backend()
    backend.detachShipsFromFleet(tableStore.ships.selectedRowIds)
    tableStore.ships.selectedRowIds = []
    tableStore.ships.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

/**
 * Attach selected ships to a group
 */
const attachToGroup = () => {
  if (tableStore.ships.selectedRowIds.length > 0) {
    gameStore.modalType = 'attachToShipGroup'
    gameStore.modalData = tableStore.ships.selectedRowIds
    tableStore.ships.selectedRowIds = []
    tableStore.ships.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const detachFromGroup = () => {
  if (tableStore.ships.selectedRowIds.length > 0) {
    const backend = new Backend()
    backend.detachShipsFromGroup(tableStore.ships.selectedRowIds)
    tableStore.ships.selectedRowIds = []
    tableStore.ships.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const destroyFleet = () => {
  if (tableStore.ships.selectedRowIds.length > 0) {
    gameStore.modal2Type = 'confirmationDelete'
    gameStore.modal2Data = {
      title: t('common.menu.ships.destroyConfirmTitle'),
      message: t('common.menu.ships.destroyConfirmMessage'),
      actionText: t('common.actions.yes'),
      cancelText: t('common.actions.no'),
      action: () => {
        const backend = new Backend()
        backend.destroyShips(tableStore.ships.selectedRowIds)
        tableStore.ships.selectedRowIds = []
        tableStore.ships.selectedAll = false

        // Disable area selection on the map
        mapStore.disableAreaSelection()
      }
    }
  }
}

function closeMenu() {
  gameStore.pullDownMenuVisible = ''
}

function handleClickOutside(event) {
  if (!menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Calculate dropdown position to keep it on screen
const dropdownPosition = computed(() => {
  if (!menuRef.value) return { top: '100%', left: '0' }

  // If smart positioning is disabled, always use default positioning
  if (!props.smartPositioning) {
    return { top: '100%', left: '0', transform: '' }
  }

  const menuWidth = 224 // w-56 = 224px
  const menuHeight = 280 // Approximate height based on menu items

  // Get map container dimensions
  const mapContainer = document.querySelector('.webgl-container')
  if (!mapContainer) return { top: '100%', left: '0' }

  const containerRect = mapContainer.getBoundingClientRect()
  const triggerRect = menuRef.value.parentElement?.getBoundingClientRect()

  if (!triggerRect) return { top: '100%', left: '0' }

  let top = '100%'
  let left = '0'
  let transform = ''

  // Check if dropdown would go off bottom of container
  if (triggerRect.bottom + menuHeight > containerRect.bottom) {
    // Flip to appear above, but still connected to the icon
    top = '0'
    transform = 'translateY(-100%)'
  }

  // Check if dropdown would go off right side of container
  if (triggerRect.left + menuWidth > containerRect.right) {
    // Align right edge of dropdown with right edge of trigger
    left = `-${menuWidth - triggerRect.width}px`
  }

  return { top, left, transform }
})
</script>

<template>
  <div class="relative" @click.stop="toggleMenu">
    <!-- Icon Button -->
    <slot></slot>
    <!-- Dropdown Menu -->
    <Transition name="menu">
      <div
        v-show="gameStore.pullDownMenuVisible === 'shipMultiSelectMenu'"
        ref="menuRef"
        class="absolute w-56 rounded-lg border border-metal-800 bg-metal-950 z-50 overflow-hidden"
        :style="{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          transform: dropdownPosition.transform
        }"
      >
        <!-- Header -->
        <div class="menu-header text-center text-metal-300 text-sm py-2 px-3">
          {{ t('common.menu.ships.selected', tableStore.ships.selectedRowIds.length) }}
        </div>

        <!-- Menu Items -->
        <div class="border-t border-metal-900">
          <div
            v-if="props.lockedToFleetId === 0"
            class="menu-item"
            @click="actionMap"
          >
            <GenericIcon type="cmd_move" class="mr-3" />
            <span>{{ t('common.menu.ships.commandOnMap') }}</span>
          </div>

          <div class="menu-item text-red-400" @click="destroyFleet">
            <GenericIcon type="trash" class="mr-3" />
            <span>{{ t('common.menu.ships.destroyShips') }}</span>
          </div>

          <!-- Divider -->
          <div class="divider"></div>

          <div class="menu-item" @click="assignToFleet">
            <GenericIcon type="add" class="mr-3" />
            <span>{{ t('common.menu.ships.attachToFleet') }}</span>
          </div>

          <div class="menu-item" @click="detachFromFleet">
            <GenericIcon type="remove" class="mr-3" />
            <span>{{ t('common.menu.ships.detachFromFleet') }}</span>
          </div>

          <div v-if="shipGroupStore.shipGroups.size > 0 && props.lockedToFleetId === 0">
            <!-- Divider -->
            <div class="divider"></div>

            <div class="menu-item" @click="attachToGroup">
              <GenericIcon type="add" class="mr-3" />
              <span>{{ t('common.menu.ships.addToGroup') }}</span>
            </div>

            <div class="menu-item" @click="detachFromGroup">
              <GenericIcon type="remove" class="mr-3" />
              <span>{{ t('common.menu.ships.removeFromGroup') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.menu-header {
  background: linear-gradient(to right, theme('colors.metal.800'), theme('colors.metal.900'));
}

.menu-item {
  @apply flex items-center p-2 text-metal-300 text-sm cursor-pointer;
  transition: background-color 150ms ease;

  &:hover {
    @apply bg-metal-800;
  }

  &:active {
    @apply bg-metal-700;
  }
}

.divider {
  @apply h-px mx-2 my-1 bg-metal-800;
}

/* Menu transition */
.menu-enter-active,
.menu-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
